import { cn } from '../lib/cn';

type AvatarProps = {
    src?: string | null;
    /** Used for the initial + alt text when there's no image. */
    name?: string | null;
    /** Stable seed for the fallback color (e.g. the user id). */
    seed?: number;
    className?: string;
};

// Deterministic palette so the same user always gets the same fallback color
// (no Math.random — keep it pure of the seed).
const PALETTE = [
    'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
];

function initial(name?: string | null) {
    const c = name?.trim()?.[0];
    return c ? c.toUpperCase() : '?';
}

// Circular avatar: renders the image when present, otherwise a colored initial.
// Size + ring are set by the caller via `className` (defaults to h-10 w-10).
export function Avatar({ src, name, seed = 0, className }: AvatarProps) {
    const base = cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        'h-10 w-10',
        className,
    );
    if (src) {
        return <img src={src} alt={name ?? ''} className={cn(base, 'object-cover')} />;
    }
    const color = PALETTE[Math.abs(seed) % PALETTE.length];
    return (
        <span aria-hidden="true" className={cn(base, 'text-sm font-semibold', color)}>
            {initial(name)}
        </span>
    );
}

// Builds the Discord CDN avatar URL from the hash, or null when not linked.
export function discordAvatarUrl(
    discordId?: string | null,
    discordAvatar?: string | null,
): string | null {
    if (!discordId || !discordAvatar) return null;
    return `https://cdn.discordapp.com/avatars/${discordId}/${discordAvatar}.png`;
}
